import { useEffect, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

function MapView({ country, state, onStateSelect, states }) {
  const mapRef = useRef(null);
  const markersRef = useRef([]);
  const mapInstanceRef = useRef(null);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    // Initialize map only once
    if (!mapInstanceRef.current) {
      // Center coordinates based on country
      let centerCoords, zoomLevel;
      if (country === 'usa') {
        centerCoords = [39.8283, -98.5795];
        zoomLevel = 4;
      } else if (country === 'canada') {
        centerCoords = [56.1304, -106.3468];
        zoomLevel = 3;
      } else if (country === 'uk') {
        centerCoords = [54.5, -2];
        zoomLevel = 6;
      }
      
      // Initialize map
      mapInstanceRef.current = L.map(mapRef.current, {
        center: centerCoords,
        zoom: zoomLevel,
        zoomControl: true,
        scrollWheelZoom: true
      });

      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '© OpenStreetMap contributors',
        maxZoom: 19,
      }).addTo(mapInstanceRef.current);
    }

    // Clear existing markers
    markersRef.current.forEach(marker => {
      if (marker && marker.remove) {
        marker.remove();
      }
    });
    markersRef.current = [];

    // Function to handle state selection from map
    const handleStateSelect = (stateName) => {
      if (onStateSelect) {
        onStateSelect(stateName);
      }
    };

    // Add markers for all states passed from CountrySelector
    if (states && states.length > 0) {
      states.forEach(stateItem => {
        const stateName = stateItem.name;
        const coords = [stateItem.lat, stateItem.lng];
        
        if (!coords || !coords[0] || !coords[1]) {
          console.warn(`Missing coordinates for ${stateName}`);
          return;
        }

        const isSelected = state && stateName === state;
        
        // Create custom mosque icon
        const iconColor = isSelected ? '#f59e0b' : '#9ca3af';
        const iconSize = isSelected ? '50px' : '40px';
        const borderWidth = isSelected ? '3px' : '2px';
        
        const mosqueIcon = L.divIcon({
          html: `
            <div class="text-white rounded-full shadow-lg flex items-center justify-center" 
                 style="width: ${iconSize}; height: ${iconSize}; background-color: ${iconColor}; border: ${borderWidth} solid white;">
              <span class="text-lg">🕌</span>
            </div>
          `,
          className: 'custom-marker',
          iconSize: isSelected ? [50, 50] : [40, 40],
          iconAnchor: isSelected ? [25, 50] : [20, 40]
        });

        const marker = L.marker(coords, { icon: mosqueIcon }).addTo(mapInstanceRef.current);
        
        // Create popup content
        const popupContent = isSelected ? 
          `<div class="p-3 min-w-[250px]">
            <div class="flex items-center mb-3">
              <div class="w-3 h-3 bg-yellow-500 rounded-full mr-2"></div>
              <strong class="text-lg">${stateName}</strong>
            </div>
            <div class="bg-yellow-50 p-3 rounded-lg mb-3">
              <p class="text-sm text-gray-700 font-medium">Currently Selected</p>
              <p class="text-xs text-gray-600 mt-1">Viewing prayer times & community data</p>
            </div>
            <p class="text-xs text-gray-500">Click on other markers to view their data</p>
          </div>` : 
          `<div class="p-3 min-w-[250px]">
            <div class="flex items-center mb-3">
              <div class="w-3 h-3 bg-gray-400 rounded-full mr-2"></div>
              <strong class="text-lg">${stateName}</strong>
            </div>
            <p class="text-sm text-gray-600 my-2">Click to view prayer times for ${stateName}</p>
            <button 
              id="select-${stateName.replace(/\s+/g, '-').replace(/[^a-zA-Z0-9-]/g, '')}-btn"
              class="w-full mt-3 bg-yellow-500 hover:bg-yellow-600 text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200 flex items-center justify-center"
            >
              <span>🕌</span>
              <span class="ml-2">View Prayer Times</span>
            </button>
          </div>`;
        
        // Bind popup with content
        const popup = L.popup({
          maxWidth: 300,
          className: 'custom-popup'
        }).setContent(popupContent);
        
        marker.bindPopup(popup);
        
        // If this is the selected state, center map on it
        if (isSelected) {
          const zoom = country === 'uk' ? 8 : 7;
          mapInstanceRef.current.setView(coords, zoom);
          setTimeout(() => {
            marker.openPopup();
          }, 300);
        }

        // Add click event to marker
        marker.on('click', function() {
          if (!state || stateName !== state) {
            handleStateSelect(stateName);
          }
        });

        // Add event listener for the button inside popup
        marker.on('popupopen', function() {
          const selectBtn = document.getElementById(`select-${stateName.replace(/\s+/g, '-').replace(/[^a-zA-Z0-9-]/g, '')}-btn`);
          if (selectBtn) {
            selectBtn.onclick = function(e) {
              e.stopPropagation();
              handleStateSelect(stateName);
            };
          }
        });

        // Store marker reference
        markersRef.current.push(marker);
      });
    }

    // Cleanup function
    return () => {
      markersRef.current.forEach(marker => {
        if (marker && marker.remove) {
          marker.remove();
        }
      });
      markersRef.current = [];
    };
  }, [country, state, onStateSelect, states]);

  // Cleanup map on unmount
  useEffect(() => {
    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, []);

  return <div ref={mapRef} className="w-full h-[500px] rounded-xl" />;
}

export default MapView;